/**
 * Atbash SDK for Node.js — public surface.
 *
 * Crypto / redaction / memory primitives are re-exported from the NAPI-bound
 * Rust core as plain functions; the HTTP-facing surface (`judgeAction`,
 * `logToolCall`, `get*`) lives on the `Atbash` class.
 */
import { native } from "./native.js";
import type {
  AgentAuth,
  KeyPair,
  MemoryDiffResult,
  MemoryEntry,
  MemorySnapshot,
  RedactResult,
} from "./types.js";

export { Atbash } from "./client.js";
export {
  DEFAULT_BLOCKCHAIN_RID,
  DEFAULT_CHROMIA_NODE_URLS,
  DEFAULT_ENDPOINT,
} from "./constants.js";
export { AtbashAPIError, SignatureVerificationError } from "./errors.js";
export {
  normalizeVerdict,
  normalizeStatus,
  pubkeyToHex,
} from "./normalize.js";
export type * from "./types.js";

// config loading (file/env resolution, endpoint validation, key file)
export {
  type AtbashUserConfig,
  getConfigDir,
  getConfigPath,
  loadUserConfig,
  saveUserConfig,
  resolve,
} from "./userConfig.js";
export {
  type JudgeEndpointConfig,
  type ValidatedEndpoint,
  validateJudgeEndpoint,
  buildAllowedJudgeHosts,
} from "./endpoint.js";
export { resolveKeyPath, loadAgentFromFile } from "./keyLoader.js";
export { redactJsonStrings, type SecretKind } from "./redact.js";
export { verifyJudgeResponseSignature } from "./signature.js";

// Memory-poisoning surface — crypto + regex pre-filter live in the Rust
// core, `scanMemory` composes them with the HTTP LLM-judge call, and
// `chain.ts` handles direct Postchain commit/rollback.
export {
  deriveMemoryKey,
  encryptMemoryContent,
  decryptMemoryContent,
  scanMemory,
  scanMemoryBatch,
  commitMemoryVersion,
  rollbackMemory,
  getActiveMemory,
  getActiveMemoryId,
  getAllAgentMemory,
  getMemoryHistory,
  getMemoryById,
  getRollbackHistory,
  classifyMemoryWrite,
  DEFAULT_MEMORY_PATH_PATTERNS,
  DEFAULT_MEMORY_WRITE_TOOL_NAMES,
  guardMemoryWrite,
  syncLocalMemory,
  MemoryIntegrityError,
  PointerStore,
  defaultPointerPath,
  createFileLogger,
  defaultPluginLogPath,
  classifyMemoryRead,
  DEFAULT_MEMORY_READ_TOOL_NAMES,
  MemoryGuardManager,
  createMemoryGuardManager,
  type CommitMemoryOptions,
  type RollbackMemoryOptions,
  type AgentMemoryEntry,
  type MemoryRollbackEvent,
  type ClassifierToolContext,
  type ClassifierToolEvent,
  type ClassifyMemoryWriteOptions,
  type GuardMemoryWriteInput,
  type GuardMemoryWriteResult,
  type GuardMemoryDecision,
  type GuardLogger,
  type MemoryPointer,
  type SyncMemoryOptions,
  type SyncMemoryResult,
  type WrappedLogger,
  type ClassifyMemoryReadOptions,
  type MemoryGuardManagerOptions,
  type HookDecision,
} from "./memory/index.js";
export {
  flushTelemetry,
  recordCall,
  recordDuration,
  setupTelemetry,
  shutdownTelemetry,
  type ClientSource,
  type TelemetryConfig,
} from "./opentel/telemetry.js";

/* ── crypto / redaction / memory primitives (Rust core) ────────────────── */

export function isValidPrivateKey(hex: string): boolean {
  return native.isValidPrivateKey(hex);
}

export function derivePublicKey(privkey: string): string {
  return native.derivePublicKey(privkey);
}

export function generateKeypair(): KeyPair {
  return native.generateKeypair();
}

export function loadAgent(privkey: string): AgentAuth {
  return native.loadAgent(privkey);
}

export function signLogToolCall(
  toolCallId: string,
  action: string,
  context: string,
  toolName: string,
  toolArgsJson: string,
  privkey: string,
  blockchainRid: string,
): string {
  return native.signLogToolCall(
    toolCallId,
    action,
    context,
    toolName,
    toolArgsJson,
    privkey,
    blockchainRid,
  );
}

export {
  signEncryptedToolCall,
  normalizeActionForHash,
  claimHashHex,
  columnAad,
} from "./encrypted-toolcall.js";
export {
  encryptForOrg,
  decryptForOrg,
  encryptedLength,
  EciesDomain,
} from "./crypto/ecies.js";
export {
  packEnvelope,
  parseEnvelope,
  isEnvelope,
  keyFingerprintOf,
  type Envelope,
} from "./crypto/envelope.js";

export function signJudgeAction(
  judgmentId: string,
  action: string,
  context: string,
  extra: string,
  privkey: string,
  blockchainRid: string,
): string {
  return native.signJudgeAction(
    judgmentId,
    action,
    context,
    extra,
    privkey,
    blockchainRid,
  );
}

export function verifySignature(
  body: Buffer,
  signatureHex: string,
  pubkeyHex: string,
): boolean {
  return native.verifySignature(body, signatureHex, pubkeyHex);
}

export function normalizeForMatching(text: string): string {
  return native.normalizeForMatching(text);
}

export function containsEvasionCharacters(text: string): boolean {
  return native.containsEvasionCharacters(text);
}

export function redactSecrets(text: string): RedactResult {
  return native.redactSecrets(text);
}

export function containsSecret(text: string): boolean {
  return native.containsSecret(text);
}

export function createMemorySnapshot(
  entries: MemoryEntry[],
  takenAt: number,
): MemorySnapshot {
  return native.createMemorySnapshot(entries, takenAt);
}

export function diffMemorySnapshots(
  before: MemorySnapshot,
  after: MemorySnapshot,
): MemoryDiffResult {
  return native.diffMemorySnapshots(before, after);
}
